import type { RootState } from '@/app/store'
import type { PageConfigRequest } from '@/types/api/page-config'

import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit'

import { ProposalAccountDetailsTabs } from '@/components/molecules/proposal-account-details'
import { updatePageConfig } from '@/legacy-api/page-config-slice'
import { ProposalGroup } from '@/types/enums/proposals'
import { Page } from '@/types/schema/page-configs'
import {
  PROPOSALS_SCHEMA_VERSION,
  type ProposalsConfig,
} from '@/types/schema/page-configs/proposals'

import {
  defaultProposalsConfig,
  ProposalTableType,
} from './defaults/page-configs/proposals'

interface ProposalsState {
  config: ProposalsConfig
  configVersion: string
  defaultConfig: ProposalsConfig
  searchTerm: string | null
  accountDetailsTab?: ProposalAccountDetailsTabs
  selectedOrder: string | null
  selectedAccount: string | null
  selectedTable: ProposalTableType
  selectedSleeve?: string
  isin?: string | null
  activeTab: ProposalGroup
}

const initialState: ProposalsState = {
  config: defaultProposalsConfig,
  configVersion: PROPOSALS_SCHEMA_VERSION,
  defaultConfig: defaultProposalsConfig,
  searchTerm: null,
  accountDetailsTab: ProposalAccountDetailsTabs.Summary,
  selectedOrder: null,
  selectedAccount: null,
  selectedTable: ProposalTableType.Accounts,
  selectedSleeve: undefined,
  isin: null,
  activeTab: ProposalGroup.ExistingClients,
}

const proposalsSlice = createSlice({
  name: 'proposals',
  reducerPath: 'proposals',
  initialState,
  reducers: {
    initializeProposalsConfig: handleInitializeProposalsConfig,
    resetProposalsConfig: handleResetProposalsConfig,
    setProposalsConfig: handleSetProposalsConfig,
    setSearchTerm: handleSetSearchTerm,
    setSelectedAccount: handleSetSelectedAccount,
    setSelectedTable: handleSetSelectedTable,
    setSelectedOrder: handleSetSelectedOrder,
    setSelectedSleeve: handleSetSelectedSleeve,
    setAccountDetailsTab: handleSetAccountDetailsTab,
    setIsin: handleSetIsin,
    setActiveTab: handleSetActiveTab,
  },
})

function handleInitializeProposalsConfig(
  state: ProposalsState,
  action: PayloadAction<{ config: ProposalsConfig; version: string }>,
) {
  state.config = action.payload.config
  state.configVersion = action.payload.version
  state.defaultConfig = action.payload.config
  state.selectedAccount = null
}

function handleResetProposalsConfig(state: ProposalsState) {
  state.config = state.defaultConfig
}

function handleSetProposalsConfig(
  state: ProposalsState,
  action: PayloadAction<Partial<ProposalsConfig>>,
) {
  const updatedConfig = {
    ...state.config,
    ...action.payload,
  }
  state.config = updatedConfig
}

function handleSetSelectedAccount(
  state: ProposalsState,
  action: PayloadAction<string>,
) {
  state.selectedAccount = action.payload
}

function handleSetAccountDetailsTab(
  state: ProposalsState,
  action: PayloadAction<ProposalAccountDetailsTabs>,
) {
  state.accountDetailsTab = action.payload
}

function handleSetSelectedSleeve(
  state: ProposalsState,
  action: PayloadAction<string>,
) {
  state.selectedSleeve = action.payload
}

function handleSetSelectedOrder(
  state: ProposalsState,
  action: PayloadAction<string>,
) {
  state.selectedOrder = action.payload
}

function handleSetSelectedTable(
  state: ProposalsState,
  action: PayloadAction<ProposalTableType>,
) {
  state.selectedTable = action.payload
}

function handleSetSearchTerm(
  state: ProposalsState,
  action: PayloadAction<string>,
) {
  state.searchTerm = action.payload
}

function handleSetIsin(state: ProposalsState, action: PayloadAction<string>) {
  state.isin = action.payload
}

function handleSetActiveTab(
  state: ProposalsState,
  action: PayloadAction<ProposalGroup>,
) {
  state.activeTab = action.payload
}

export const persistProposalsConfigAsyncThunk = createAsyncThunk(
  // biome-ignore lint/style/useTemplate: Bulk suppression as part of biome migration
  proposalsSlice.reducerPath + '/persistProposalsConfig',
  async (_data, { dispatch, getState, rejectWithValue }) => {
    const state = (getState() as RootState).proposals
    try {
      const request: PageConfigRequest = {
        id: Page.Proposals,
        config: state.config,
        version: state.configVersion,
      }
      await dispatch(updatePageConfig.initiate(request))
    } catch (err) {
      const errorMessage =
        // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
        (err as { response?: { data?: any } }).response?.data ||
        'An unknown error occurred'
      return rejectWithValue(errorMessage)
    }
  },
)

export const {
  initializeProposalsConfig,
  resetProposalsConfig,
  setProposalsConfig,

  setActiveTab,
} = proposalsSlice.actions

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default proposalsSlice
